import { Card, Group, Skeleton, Flex } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useCallback, useState } from "react";
import RefreshQueryButton from "@/components/refresh-query-button";
import { IDEIAS_COLLECTION_KEY } from "@/modules/ideias/firestore";
import ExportExcel from "@/modules/ideias-relatorio-visao-geral/components/ExportExcel";
import FiltrosAtivos from "@/modules/ideias-relatorio-visao-geral/components/FiltrosAtivos";
import ModalFilterIdeias, { FiltroIdeia } from "@/modules/ideias-relatorio-visao-geral/components/ModalFilterIdeias";
import RelatorioVisaoGeralCard from "@/modules/ideias-relatorio-visao-geral/components/RelatorioVisaoGeralCard";
import { useIdeiasRelatorio } from "@/modules/ideias-relatorio-visao-geral/hooks/useIdeiasRelatorio";
import useUserStore from "@/modules/users/store";
import RelatorioVisaoGeralIdeiasTable from "@/src/modules/ideias-relatorio-visao-geral/components/RelatorioVisaoGeralIdeiasTable";

const DIAS_MES = 30;
const REFRESH_ALL_KEY = [IDEIAS_COLLECTION_KEY];
const HORA_INICIO = 0;
const MINUTO_INICIO = 0;
const SEGUNDO_INICIO = 0;
const MILISSEGUNDO_INICIO = 0;
const HORA_FIM = 23;
const MINUTO_FIM = 59;
const SEGUNDO_FIM = 59;
const MILISSEGUNDO_FIM = 999;

interface DateFormat {
  dataInicial: Date;
  dataFinal: Date;
}

export interface Cards {
  colaboradoresAtivos: number;
  ideias: {
    total: number;
    participantes: Set<string>;
    porcentagem: number;
  };
  curtidas: {
    total: number;
    participantes: Set<string>;
    porcentagem: number;
  };
  comentarios: {
    total: number;
    participantes: Set<string>;
    porcentagem: number;
  };
}

const mockFilterIdeias: FiltroIdeia = {
  search: "",
  campo: "",
  status: ""
};

// prettier-ignore
const RelatorioVisaoGeralIdeiasPage = () => {
  const [date, setDate] = useState<DateFormat>(() => {
    const dataInicial = new Date(new Date().setDate(new Date().getDate() - DIAS_MES));
    dataInicial.setHours(HORA_INICIO, MINUTO_INICIO, SEGUNDO_INICIO, MILISSEGUNDO_INICIO);
    
    const dataFinal = new Date();
    dataFinal.setHours(HORA_FIM, MINUTO_FIM, SEGUNDO_FIM, MILISSEGUNDO_FIM);
    
    return { dataInicial, dataFinal };
  });
  const [datePickerValue, setDatePickerValue] = useState<[Date | null, Date | null]>([
    new Date(new Date().setDate(new Date().getDate() - DIAS_MES)),
    new Date()
  ]);
  const empresa = useUserStore((state) => state.activeEmpresa);
  const [filterIdeias, setFilterIdeias] = useState<FiltroIdeia>(mockFilterIdeias);

  const { cards, ideiasProcessadas, isLoading } = useIdeiasRelatorio(date, filterIdeias);

  const onDateIniciaFinalChange = useCallback((dates: [Date | null, Date | null]) => {
    setDatePickerValue(dates);
    const [dataInicial, dataFinal] = dates;
    if (dataInicial && dataFinal) {
      // Ajustar data inicial para 00:00:00
      const dataInicialAjustada = new Date(dataInicial);
      dataInicialAjustada.setHours(HORA_INICIO, MINUTO_INICIO, SEGUNDO_INICIO, MILISSEGUNDO_INICIO);
      
      // Ajustar data final para 23:59:59
      const dataFinalAjustada = new Date(dataFinal);
      dataFinalAjustada.setHours(HORA_FIM, MINUTO_FIM, SEGUNDO_FIM, MILISSEGUNDO_FIM);
      
      setDate({ 
        dataInicial: dataInicialAjustada, 
        dataFinal: dataFinalAjustada 
      });
    }
  }, []);


  return (
    <Card withBorder shadow="md">
      <Card.Section inheritPadding py="md" withBorder>
        <Flex justify="space-between">
          <DatePickerInput
            type="range"
            w="340px"
            valueFormat="DD [de] MMMM, YYYY"
            value={datePickerValue}
            onChange={onDateIniciaFinalChange}
            label="Período"
            maxDate={new Date()}
          />
          <ExportExcel
            cards={cards}
            empresa={empresa}
            dadosFiltrados={ideiasProcessadas}
            isLoading={isLoading}
          />
        </Flex>
      </Card.Section>
      <Card.Section inheritPadding py="md" withBorder>
        <Group justify="space-between">
          <Group>
            <ModalFilterIdeias setFilter={setFilterIdeias} />
            <FiltrosAtivos
              filtros={filterIdeias}
              totalResultados={ideiasProcessadas.length}
            />
          </Group>
          <RefreshQueryButton queryKey={REFRESH_ALL_KEY} />
        </Group>
      </Card.Section>
      {isLoading ? (
        <Group justify="center" mt="md" mb="md" gap="xs">
          <Skeleton height={300} width={200} />
          <Skeleton height={300} width={200} />
          <Skeleton height={300} width={200} />
        </Group>
      ) : (
        <Group justify="center" mt="md" mb="md" gap="xs">
          <RelatorioVisaoGeralCard cards={cards} tipo="ideias" />
          <RelatorioVisaoGeralCard cards={cards} tipo="comentarios" />
          <RelatorioVisaoGeralCard cards={cards} tipo="curtidas" />
          <RelatorioVisaoGeralCard cards={cards} tipo="resumo" />
        </Group>
      )}
      <RelatorioVisaoGeralIdeiasTable
        data={ideiasProcessadas}
        isLoading={isLoading}
      />
    </Card>
  );
};

export default RelatorioVisaoGeralIdeiasPage;
